import React, { ReactNode, useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { Button, Divider, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useApiService } from "../../services/ApiServiceProvider";
import SnackbarAlert, { Severity } from '../alert/SnackbarAlert';
import TopNavActions from './TopNavActions';
import AgreementTermsView from './AgreementTermsView';
import {
  visionOptions,
  hearingOptions,
  physicalOptions,
  cognitiveOptions,
} from './models/AccessibilityPreferences';


interface ReviewAndEnrollViewProps {
  enrollment: any;
  onPageChange: (page: string) => void;
}

const ReviewAndEnrollView: React.FC<ReviewAndEnrollViewProps> = ({ enrollment, onPageChange }) => {
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [acknowledgementAccepted, setAcknowledgementAccepted] = useState(false);
  const [agreementTerms, setAgreementTerms] = useState<string[]>([]);
  const [selectedVisionOptions, setSelectedVisionOptions] = useState<any[]>([]);
  const [selectedHearingOptions, setSelectedHearingOptions] = useState<any[]>([]);
  const [selectedPhysicalOptions, setSelectedPhysicalOptions] = useState<any[]>([]);
  const [selectedCognitiveOptions, setSelectedCognitiveOptions] = useState<any[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarTitle, setSnackbarTitle] = useState('');
  const [snackbarContent, setSnackbarContent] = useState<ReactNode>();
  const [snackbarSeverity, setSnackbarSeverity] = useState<Severity>('info');
  const { t } = useTranslation('enrollment');

  const handleAgreementChange = (allSelected: boolean, terms: string[]) => {
    setAcknowledgementAccepted(allSelected);
    setAgreementTerms(terms);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (title: string, content: ReactNode, severity: Severity) => {
    setSnackbarTitle(title);
    setSnackbarContent(content);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
    // Close snackbar after 5 seconds
    setTimeout(() => {
      handleSnackbarClose();
    }, 5000);
  };

  const apiService = useApiService();

  useEffect(() => {
    setSelectedVisionOptions(
      enrollment?.accessibilityPreferences?.vision?.length > 0
        ? visionOptions.filter(x => enrollment.accessibilityPreferences.vision.includes(x.name))
        : []
    );
    setSelectedHearingOptions(
      enrollment?.accessibilityPreferences?.hearing?.length > 0
        ? hearingOptions.filter(x => enrollment.accessibilityPreferences.hearing.includes(x.name))
        : []
    );
    setSelectedPhysicalOptions(
      enrollment?.accessibilityPreferences?.physical?.length > 0
        ? physicalOptions.filter(x => enrollment.accessibilityPreferences.physical.includes(x.name))
        : []
    );
    setSelectedCognitiveOptions(
      enrollment?.accessibilityPreferences?.cognitive?.length > 0
        ? cognitiveOptions.filter(x => enrollment.accessibilityPreferences.cognitive.includes(x.name))
        : []
    );
    // eslint-disable-next-line
  }, [enrollment]);

  const handleDecline = () => {
    onPageChange('enrollment-declined-view');
  };

  const handleEnroll = async () => {
    if (!acknowledgementAccepted || isLoadingData) {
      return;
    }
    enrollment.enrolled = true;
    enrollment.completedAt = new Date();
    enrollment.agreementTerms = agreementTerms;
    setIsLoadingData(true);
    try {
      await apiService.enrollmentCreate(enrollment);
      onPageChange('enrollment-completed-view');
    } catch (error:Error | any) {
      setIsLoadingData(false);
      showSnackbar(
        t("Error"),
        error?.message || t("An error occurred while registering. Please try again."),
        'error'
      );
    }
  };

  const handleBack = () => {
    onPageChange('back');
  };

  const handleEnrollClick = () => {
    if (!acknowledgementAccepted) {
      showSnackbar(
        t('Action required'),
        t('Please review and accept the acknowledgment terms before registering.'),
        'warning'
      );
      return;
    }
    if (!isLoadingData) {
      handleEnroll();
    }
  };

  const OptionList = ({ title, options }: { title: string, options: any[] }) => (
    <div>
      <Typography variant="h5" component='div'>{title}</Typography>
      {options.length > 0 ? (
        <List sx={{ display: 'inline', p: 0 }}>
          {options.map((item) => (
            <ListItem key={item.name} sx={{ display: 'inline', p: 0 }}>
              <ListItemText primary={t(item.displayName)} />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body1">{t("None")}</Typography>
      )}
    </div>
  );

  const LabeledValueStack = ({ items }: { items: { label: string; value: string }[] }) => {
    return (
      <Stack spacing={2}>
        {items.map((item, idx) => (
          <React.Fragment key={item.label}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body1">{t(item.label)}:</Typography>
              <Typography variant="body1">{t(item.value)}</Typography>
            </Stack>
            {idx < items.length - 1 && <Divider sx={{ borderColor: 'black' }} />}
          </React.Fragment>
        ))}
      </Stack>
    );
  }

  return (
    <React.Fragment>
      <TopNavActions
        onBack={handleBack}
        disabled={isLoadingData}
        hideExit={true}
      />

      <Typography variant="h4" component='h1' id="page-heading" tabIndex={-1} sx={{mb: 2}}>{t("Review and Register")}</Typography>
      <Typography variant="body1" component='div'>
        {t("Please review your answers reading all the way through the bottom and select an option. If you have any questions, you can still proceed with enrollment and ask or make changes at a later time.")}
      </Typography>

  <Typography variant="h6" component='div' sx={{ mt: 4 }} >{t("Contact Information").toUpperCase()}</Typography>
      <Card style={{padding: '1rem', marginBottom: '1rem'}}>
        <LabeledValueStack
          items={[
            { label: 'First Name', value: enrollment.subjectInformation.firstName },
            { label: 'Last Name', value: enrollment.subjectInformation.lastName },
            { label: 'Phone Number', value: enrollment.subjectInformation.phoneNumber },
            { label: 'Email', value: enrollment.subjectInformation.emailAddress },
          ]}
        />
      </Card>
      {/* Languages */}
      <Typography variant="h6" component='div' sx={{ mt: 4 }}>{t("Languages").toUpperCase()}</Typography>
      <Card style={{padding: '1rem', marginBottom: '1rem'}}>
        <LabeledValueStack
          items={[
            { label: 'Primary language', value: t(enrollment.languageInformation.primaryLanguage) },
            ...(
              enrollment.languageInformation.specifiedPrimaryLanguage && enrollment.languageInformation.specifiedPrimaryLanguage !== ''
                ? [
                    {
                      label: 'Specified primary language',
                      value: t(enrollment.languageInformation.specifiedPrimaryLanguage),
                    },
                  ]
                : []
            ),
            {
              label: 'Additional languages',
              value:
                (enrollment.languageInformation.additionalLanguages || []).length > 0
                  ? (enrollment.languageInformation.additionalLanguages || []).map((lang: string) => t(lang)).join(', ')
                  : t('None'),
            },
            ...(
              enrollment.languageInformation.specifiedAdditionalLanguages && enrollment.languageInformation.specifiedAdditionalLanguages !== ''
                ? [
                    {
                      label: 'Other',
                      value: enrollment.languageInformation.specifiedAdditionalLanguages,
                    },
                  ]
                : []
            ),
          ]}
        />
      </Card>
      <Card style={{padding: '1rem', marginBottom: '1rem'}}>
        <LabeledValueStack
          items={[
            { label: 'Date of birth', value: enrollment.subjectInformation.dateOfBirth }
          ]}
        />
      </Card>

      <Typography variant="h6" component='div' sx={{ mt: 4 }}>{t("Accessibility Preferences").toUpperCase()}</Typography>
      <Card style={{padding: '1rem', marginBottom: '1rem'}}>
        <OptionList title={t("Vision")} options={selectedVisionOptions} />
        <Divider sx={{ my: 2, borderBottomWidth: 1, borderColor: 'black' }} />
        <OptionList title={t("Hearing")} options={selectedHearingOptions} />
        <Divider sx={{ my: 2, borderBottomWidth: 1, borderColor: 'black' }} />
        <OptionList title={t("Physical")} options={selectedPhysicalOptions} />
        <Divider sx={{ my: 2, borderBottomWidth: 1, borderColor: 'black' }} />
        <OptionList title={t("Cognitive")} options={selectedCognitiveOptions} />
      </Card>
      <Typography variant="h6" component='div' sx={{ mt: 4 }}>{t("Selected Disorders").toUpperCase()}</Typography>
      <Card style={{padding: '1rem', marginBottom: '1rem'}}>
        <OptionList
          title={t("Voice Disorders")}
          options={(enrollment.disorderInformation.selectedVoiceDisorders || []).map((item: string) => ({ name: item, displayName: item }))}
        />
        <Divider sx={{ my: 2, borderBottomWidth: 1, borderColor: 'black' }} />
        <OptionList
          title={t("Neurological and Neurodegenerative Disorders")}
          options={(enrollment.disorderInformation.selectedNeurologicalDisorders || []).map((item: string) => ({ name: item, displayName: item }))}
        />
        <Divider sx={{ my: 2, borderBottomWidth: 1, borderColor: 'black' }} />
        <OptionList
          title={t("Mood and Psychiatric Disorders")}
          options={(enrollment.disorderInformation.selectedPsychiatricDisorders || []).map((item: string) => ({ name: item, displayName: item }))}
        />
        <Divider sx={{ my: 2, borderBottomWidth: 1, borderColor: 'black' }} />
        <OptionList
          title={t("Respiratory Disorders")}
          options={(enrollment.disorderInformation.selectedRespiratoryDisorders || []).map((item: string) => ({ name: item, displayName: item }))}
        />
      </Card>
      <AgreementTermsView
        isControlParticipant={enrollment.isControlParticipant}
        onSelectedChange={handleAgreementChange}
        disabled={isLoadingData}
      />
      {/* Show CircularProgress before the action buttons */}
      {isLoadingData && (
        <div style={{ minHeight: '4rem', textAlign: 'center' }}>
          <CircularProgress />
        </div>
      )}
      <SnackbarAlert
        open={snackbarOpen}
        title={snackbarTitle}
        severity={snackbarSeverity}
        content={snackbarContent}
        vertical='top'
        onClose={handleSnackbarClose}
      />
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: 100, marginTop: 32, marginBottom: 32 }}>
        <Button variant='outlined' onClick={handleDecline} disabled={isLoadingData}
          style={{
            borderColor: 'var(--mui-palette-error-main)',
            color: 'var(--mui-palette-error-main)'
          }}
        >
          {t("Decline")}
        </Button>
        <Button variant='contained'
          onClick={handleEnrollClick}
          disabled={isLoadingData}
          style={{
            display: 'inline-block'
          }}
        >
          {t("Register")}
        </Button>
      </div>
    </React.Fragment>
  );
};

export default ReviewAndEnrollView;
